import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, AlertTriangle, Landmark, Clock, Send,
         ChevronRight, ChevronDown, CheckCircle2 } from 'lucide-react';
import { dashboardApi, navUpoApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, Badge, Btn, Alert, Spinner } from '../components/UI';
import { huFmt, fmtMoney, huCompact } from '../utils';
import CashflowChart from '../components/charts/CashflowChart';
import ForecastChart from '../components/charts/ForecastChart';
import Sparkline from '../components/charts/Sparkline';
import { useCountUp } from '../hooks/useMotion';

// ── Segédek ────────────────────────────────────────────────────────────────
const fmtDate = (v) => v ? v.slice(0, 10).replace(/-/g, '.') + '.' : '–';

const BANK_COLORS = { revolut: '#7c3aed', raiffeisen: '#eab308' };
const bankColor = (b) => BANK_COLORS[(b || '').toLowerCase()] || '#0ea5e9';
const bankLabel = (b) => b ? b.charAt(0).toUpperCase() + b.slice(1) : 'Bank';

const SEVERITY = {
  danger:  { color: 'var(--danger)',  bg: 'var(--danger-subtle)'  },
  warning: { color: 'var(--warning)', bg: 'var(--warning-subtle)' },
  primary: { color: 'var(--primary)', bg: 'var(--primary-subtle)' },
  ok:      { color: 'var(--success)', bg: 'var(--success-subtle)' },
};

// Kártya-fejléc: kis kapitális címke (a Card title-slotjába)
const CardLabel = ({ children }) => (
  <span style={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.07em',
    textTransform: 'uppercase', color: 'var(--text-muted)' }}>{children}</span>
);

const HeaderLink = ({ to, children }) => (
  <Link to={to} style={{ fontSize: 'var(--fs-xs)', fontWeight: 500 }}>{children}</Link>
);

// ⚠ SANDBOX-jelzés (2026-08-06). A kártya korábban teszt-környezetből származó
// egyenlegeket mutatott valós pénzként, mindenféle jelzés nélkül — mérve
// 1 010 milliárd Ft „rendelkezésre álló pénz”, mert a Raiffeisen sandbox
// 114 mrd HUF-ot és 2× 1,23 mrd EUR-t ad vissza. A környezet-kapcsoló CSAK a
// Beállítások → Integrációk alatt látszik, tehát a nyitóképernyőn semmi nem
// árulta el. Ez ugyanaz a hibaosztály, mint a „hiba esetén ne írj 0 Ft-ot”:
// egy pénzügyi felület nem állíthat valótlant a rendelkezésre álló pénzről.
function SandboxChip({ banks }) {
  if (!banks || banks.length === 0) return null;
  return (
    <span title={`Teszt-környezet: ${banks.map(bankLabel).join(', ')}`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
        fontSize: 'var(--fs-xs)', fontWeight: 700, letterSpacing: '0.06em',
        textTransform: 'uppercase', color: 'var(--warning)',
        background: 'var(--warning-subtle)', border: '1px solid var(--warning)',
        borderRadius: 'var(--radius)', padding: '0.2rem 0.45rem', whiteSpace: 'nowrap' }}>
      <AlertTriangle size={12} style={{ flexShrink: 0 }} /> Sandbox
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ZÓNA 1 — KPI-csík
// ═══════════════════════════════════════════════════════════════════════════
//
// Az elv (Role–Metric–Density–Action): a nyitóképernyő EGY szerepre optimalizál
// — a tulajdonos-ügyvezetőére —, és az ő első kérdésére válaszol: „mennyi pénzem
// van és kifutok-e belőle”. A részletek (számla-bontás, adónemek) nem tűnnek el,
// csak lenyitás mögé kerülnek: korábban a bank-dobozok a nyitóképernyő harmadát
// vitték el.
//
// ⚠ Amit NEM teszünk ide: „előző hónaphoz képest ±%” a bankegyenlegre. Nincs
// hozzá adat — egyenleg-historikum sehol nem tárolódik (a BankStatement csak
// havi záró egyenleget hoz, és csak a feldolgozott kivonatokra). Kitalálni tilos;
// helyette a KIFELÉ mutató szám áll ott, ami viszont valós: a 30 napos projekció.

/**
 * Egy KPI-csempe. A szám mono + tabular-nums, ezért a felpörgetés közben
 * NEM ugrál a szélessége — ez a különbség egy kellemes és egy olcsó animáció közt.
 */
function KpiTile({
  label, value, unit = 'Ft', decimals = 0, accent,
  sub, subColor, chip, footnote, spark, sparkColor,
  state, onRetry, expand, index = 0,
}) {
  const shown = useCountUp(value ?? 0, { decimals });

  // ⚠ Milliárdos nagyságrend: 13 jegy + ezres szóközök kifolynak a csempéből
  // (mérve: 1 010 845 693 681 Ft). A rövidítést a CÉLÉRTÉK dönti el, nem a
  // pillanatnyi pörgetett szám — különben a formátum váltana animáció közben
  // („500 000 000” → „1,0 Mrd”), és ugrálna a szélesség. A pontos érték a
  // `title`-ben marad, tehát nem vész el.
  const big = Math.abs(Number(value) || 0) >= 1e9;
  const display = big ? huFmt(shown / 1e9, 1) : huFmt(shown, decimals);
  const unitText = big ? `Mrd ${unit}`.trim() : unit;
  const exact = value != null ? `${huFmt(value, decimals)} ${unit}`.trim() : undefined;

  const body = () => {
    if (state === 'error') {
      return (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 40,
            color: 'var(--warning)' }}>
            <AlertTriangle size={17} style={{ flexShrink: 0 }} />
            <span style={{ fontSize: 'var(--fs-sm)' }}>Nem tölthető be.</span>
          </div>
          {onRetry && <Btn variant="secondary" size="sm" onClick={onRetry}>Újra</Btn>}
        </>
      );
    }
    if (state === 'loading') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, height: 40 }}>
          <Spinner size={17} />
          <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
            Betöltés…
          </span>
        </div>
      );
    }
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10,
          flexWrap: 'wrap', minHeight: 40 }}>
          <span title={exact}
            style={{ fontFamily: 'var(--mono)', fontSize: '1.65rem', fontWeight: 700,
              lineHeight: 1.12, fontVariantNumeric: 'tabular-nums',
              color: accent || 'var(--text)' }}>
            {display}
            {unitText && <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)',
              fontWeight: 500 }}> {unitText}</span>}
          </span>
          {spark && spark.length > 1 && (
            <span style={{ marginLeft: 'auto', paddingBottom: 3 }}>
              <Sparkline data={spark} color={sparkColor || 'var(--text-muted)'}
                width={104} height={26} />
            </span>
          )}
        </div>
        {sub && (
          <p style={{ fontSize: 'var(--fs-xs)', color: subColor || 'var(--text-muted)',
            marginTop: 5 }}>{sub}</p>
        )}
      </>
    );
  };

  return (
    <div className="pf-rise" style={{ '--i': index, flex: '1 1 232px', minWidth: 0,
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow)',
      padding: '0.85rem 1rem', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <CardLabel>{label}</CardLabel>
        {chip && <span style={{ marginLeft: 'auto' }}>{chip}</span>}
      </div>
      {body()}
      {footnote && (
        <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-dim)', marginTop: 6 }}>
          {footnote}
        </p>
      )}
      {expand && (
        <button type="button" onClick={expand.onToggle}
          aria-expanded={expand.open}
          style={{ marginTop: 'auto', paddingTop: 10, display: 'flex', alignItems: 'center',
            gap: 5, background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-muted)', fontSize: 'var(--fs-xs)', fontWeight: 500 }}>
          {expand.label}
          <ChevronDown size={13} style={{
            transform: expand.open ? 'rotate(180deg)' : 'none', transition: 'transform .18s' }} />
        </button>
      )}
    </div>
  );
}

/**
 * Cash runway — hány hónapig futunk a jelenlegi égési ütem mellett.
 *
 * ⚠ EREDMÉNYSZEMLÉLETŰ becslés: a `monthly` a kiállított/beérkezett SZÁMLÁKAT
 * összegzi, nem a pénzmozgást (lásd a Zóna 3 megjegyzését). Ez a KPI ezért
 * közelítés — a felületen ki is írjuk. Valódi, pénzforgalmi runway a
 * BankTransaction HUF-mezője után lesz építhető.
 *
 * ⚠ A FOLYÓ hónap kimarad: az még részleges, és lefelé húzná az átlagot.
 */
function computeRunway(monthly, balanceTotal) {
  const closed = (monthly || []).slice(0, -1).slice(-6);
  if (closed.length < 3) return null;
  const nets = closed.map((m) => (m.income_huf || 0) - (m.expense_huf || 0));
  const avg = nets.reduce((s, v) => s + v, 0) / nets.length;
  const burning = avg < 0;
  return {
    avg,
    burning,
    months: burning && balanceTotal != null ? balanceTotal / -avg : null,
    sample: closed.length,
  };
}

function RunwayTile({ monthly, balanceTotal, balancesReady, index }) {
  const r = computeRunway(monthly, balanceTotal);
  const nets = (monthly || []).map((m) => (m.income_huf || 0) - (m.expense_huf || 0));

  if (!r) {
    return (
      <KpiTile index={index} label="Cash runway" value={0} unit=""
        sub="Kevés előzmény — legalább 3 lezárt hónap kell a becsléshez." />
    );
  }
  // Nem éget pénzt → a „runway” értelmetlen; a havi átlagos eredmény a hasznos szám.
  if (!r.burning) {
    return (
      <KpiTile index={index} label="Havi átlagos eredmény" value={r.avg} unit="Ft"
        accent="var(--success)" spark={nets} sparkColor="var(--chart-in)"
        sub={`${r.sample} lezárt hónap átlaga · nem éget pénzt`}
        footnote="eredményszemléletű becslés — számlák, nem pénzmozgás" />
    );
  }
  if (balanceTotal == null) {
    return (
      <KpiTile index={index} label="Cash runway" value={null}
        state={balancesReady ? 'error' : 'loading'} unit=""
        footnote="a bankegyenleg kell hozzá" />
    );
  }
  const m = r.months;
  const accent = m >= 6 ? 'var(--success)' : m >= 3 ? 'var(--warning)' : 'var(--danger)';
  return (
    <KpiTile index={index} label="Cash runway" value={m} unit="hónap" decimals={1}
      accent={accent} spark={nets} sparkColor="var(--chart-out)"
      sub={`havi átlag −${huCompact(-r.avg)} Ft · ${r.sample} lezárt hónap`}
      footnote="eredményszemléletű becslés — számlák, nem pénzmozgás" />
  );
}

// ── Lenyitható részletek (a KPI-csík ALATT, teljes szélességben) ───────────
// Szándékosan nem a csempén belül nyílik: négy csempéből egyet lenyitva a
// grid-sor mind a négyet megnyújtaná.
function DetailPanel({ which, balances, navState }) {
  if (which === 'balances') {
    if (!balances?.length) return null;
    return (
      <Card style={{ marginBottom: 14 }} title={<CardLabel>Számlák</CardLabel>}
        actions={<HeaderLink to="/statements">Kivonatok →</HeaderLink>}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {balances.map((b, i) => (
            <div key={i} title={[b.name, b.ref].filter(Boolean).join(' · ') || undefined}
              style={{ border: '1px solid var(--border)',
                borderRadius: 'var(--radius)', padding: '0.5rem 0.7rem', minWidth: 152 }}>
              <p style={{ display: 'flex', alignItems: 'center', gap: 6,
                fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', marginBottom: 4 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, flexShrink: 0,
                  background: bankColor(b.bank) }} />
                {bankLabel(b.bank)} · {b.currency}
              </p>
              <p style={{ fontFamily: 'var(--mono)', fontSize: 'var(--fs-base)',
                fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {fmtMoney(b.balance, b.currency)}
              </p>
              {b.currency !== 'HUF' && (
                /* ⚠ Ha nincs árfolyam, a HUF-érték NULL, és a sor kimarad a
                   totálból. Korábban ez „≈  Ft”-ként, üres összeggel renderelt —
                   néma kihagyás. A hiányt ki KELL írni, különben a sorok nem
                   adják ki a kártya tetején látható összeget. */
                b.balance_huf == null ? (
                  <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--warning)', marginTop: 2 }}>
                    nincs árfolyam — nincs a végösszegben
                  </p>
                ) : (
                  <p style={{ fontFamily: 'var(--mono)', fontSize: 'var(--fs-xs)',
                    color: 'var(--text-muted)', marginTop: 2 }}>
                    ≈ {huFmt(b.balance_huf)} Ft
                  </p>
                )
              )}
              {/* A számla neve + azonosító-vége: EZ különböztet meg két azonos
                  nevű számlát ugyanannál a banknál (mérve: két Raiffeisen EUR
                  számla azonos névvel és összeggel — a kártyán egyformák voltak). */}
              {(b.name || b.ref) && (
                <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-dim)', marginTop: 4,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  maxWidth: 168 }}>
                  {b.name}{b.name && b.ref ? ' · ' : ''}
                  {b.ref && <span style={{ fontFamily: 'var(--mono)' }}>{b.ref}</span>}
                </p>
              )}
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (which === 'nav') {
    const rows = (navState?.adonemek || []).filter((a) => a.egyenleg);
    if (!rows.length) return null;
    return (
      <Card style={{ marginBottom: 14 }} title={<CardLabel>NAV adónemek</CardLabel>}
        actions={<HeaderLink to="/settings/nav-upo">Részletek →</HeaderLink>}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {rows.map((a) => (
            <div key={a.adonem_kod} title={a.megnevezes || undefined}
              style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                padding: '0.4rem 0.6rem', minWidth: 104 }}>
              <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)',
                marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden',
                textOverflow: 'ellipsis', maxWidth: 150 }}>
                {a.megnevezes || a.adonem_kod}
              </p>
              <p style={{ fontFamily: 'var(--mono)', fontSize: 'var(--fs-sm)',
                fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                color: a.minosites === 'TARTOZAS' ? 'var(--danger)'
                     : a.minosites === 'TULFIZETES' ? 'var(--success)' : 'var(--text)' }}>
                {huFmt(a.egyenleg)}
              </p>
            </div>
          ))}
        </div>
      </Card>
    );
  }
  return null;
}

// ═══════════════════════════════════════════════════════════════════════════
// ZÓNA 2 — 30 napos likviditás-előrejelzés
// ═══════════════════════════════════════════════════════════════════════════
//
// A korábbi két arányos sáv MAGNITÚDÓT mutatott, pedig ez IDŐZÍTÉS-kérdés: nem
// az számít, hogy 30 nap alatt mennyi a nettó, hanem hogy a bérfizetés előtt
// beérkezik-e a vevői utalás. (Gábor döntése, 2026-08-06.)
function Fact({ label, value, color, sub, title }) {
  return (
    <div style={{ minWidth: 132 }}>
      <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{label}</p>
      <p title={title} style={{ fontFamily: 'var(--mono)', fontSize: 'var(--fs-lg)',
        fontWeight: 700, fontVariantNumeric: 'tabular-nums',
        color: color || 'var(--text)', marginTop: 2 }}>
        {value}
      </p>
      {sub && <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>{sub}</p>}
    </div>
  );
}

// Előjeles, rövidített összeg a tény-sorhoz; a pontos érték a `title`-be megy.
const signed = (v) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${huCompact(Math.abs(v || 0))} Ft`;
const exactFt = (v) => `${huFmt(v || 0)} Ft`;

function ForecastCard({ forecast, isLoading, isError, onRetry, startBalance,
                        balancePending, interval, setInterval_ }) {
  const t = forecast?.totals;
  const net = t ? t.in_huf - t.out_huf : null;
  const coverage = (t && t.out_huf > 0 && startBalance != null)
    ? startBalance / t.out_huf : null;

  const segBtn = (on) => ({
    padding: '0.28rem 0.7rem', fontSize: 'var(--fs-xs)', cursor: 'pointer',
    border: '1px solid ' + (on ? 'var(--primary)' : 'var(--border)'),
    background: on ? 'var(--tab-active-bg)' : 'transparent',
    color: on ? 'var(--tab-active-fg)' : 'var(--text-muted)', fontWeight: on ? 600 : 400,
    borderRadius: 'var(--radius)',
  });

  return (
    <Card title={<CardLabel>Következő 30 nap — likviditás</CardLabel>}
      actions={
        <div style={{ display: 'flex', gap: 4 }}>
          <button style={segBtn(interval === 'week')} onClick={() => setInterval_('week')}>Heti</button>
          <button style={segBtn(interval === 'day')} onClick={() => setInterval_('day')}>Napi</button>
        </div>
      }
      style={{ flex: '1.7 1 460px', marginBottom: 0 }}>
      {isError ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--warning)' }}>
            Az előrejelzés nem tölthető be.
          </span>
          <Btn variant="secondary" size="sm" onClick={onRetry}>Újra</Btn>
        </div>
      ) : isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center',
          height: 262 }}><Spinner size={22} /></div>
      ) : (
        <>
          <ForecastChart buckets={forecast?.buckets || []} startBalance={startBalance}
            balancePending={balancePending} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, marginTop: 14,
            paddingTop: 12, borderTop: '1px solid var(--border-dim)' }}>
            <Fact label="Várható bevétel" color="var(--chart-in)"
              value={`+${huCompact(t?.in_huf || 0)} Ft`} title={exactFt(t?.in_huf)}
              sub={`${t?.in_count || 0} számla`} />
            <Fact label="Fizetendő" color="var(--danger)"
              value={`−${huCompact(t?.out_huf || 0)} Ft`} title={exactFt(t?.out_huf)}
              sub={t?.overdue_out_count
                ? `${t.out_count} számla · ebből ${t.overdue_out_count} lejárt`
                : `${t?.out_count || 0} számla`} />
            <Fact label="Várható nettó"
              color={net >= 0 ? 'var(--success)' : 'var(--danger)'}
              value={signed(net)} title={exactFt(net)} />
            {coverage != null && (
              <Fact label="Fedezet" value={`${huFmt(coverage, 1)}×`}
                color={coverage >= 1 ? 'var(--success)' : 'var(--danger)'}
                sub="egyenleg / kötelezettség" />
            )}
          </div>
        </>
      )}
    </Card>
  );
}

// ── Teendők ────────────────────────────────────────────────────────────────
function TodoRow({ to, sev, icon: Icon, title, sub, subColor, count }) {
  const s = SEVERITY[sev] || SEVERITY.primary;
  return (
    <Link to={to} title={title} style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '0.55rem 0.65rem',
      borderLeft: `3px solid ${s.color}`, borderRadius: 'var(--radius)',
      background: 'transparent', color: 'inherit', marginBottom: 6,
      transition: 'background 0.15s' }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
      <span style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: s.bg }}>
        <Icon size={15} style={{ color: s.color }} />
      </span>
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: 'block', fontSize: 'var(--fs-sm)', fontWeight: 600 }}>
          {title}
        </span>
        {sub && (
          <span style={{ display: 'block', fontSize: 'var(--fs-xs)',
            color: subColor || 'var(--text-muted)', marginTop: 1 }}>{sub}</span>
        )}
      </span>
      {count != null && (
        <span style={{ fontFamily: 'var(--mono)', fontSize: 'var(--fs-sm)',
          fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{count}</span>
      )}
      <ChevronRight size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
    </Link>
  );
}

function TodoCard({ todo }) {
  const t = todo || {};
  // D26 (2026-09-27): nem-ADMIN-nak a backend a bért kihagyja — a felület kimondja (U2).
  const { isAdmin } = useAuth();
  const rows = [];
  if (t.overdue_count > 0) {
    rows.push({
      key: 'overdue', sev: 'danger', icon: AlertCircle, to: '/documents',
      title: 'Lejárt számla', count: t.overdue_count,
      sub: `${huFmt(t.overdue_total_huf)} Ft` + (isAdmin ? '' : ' · bér nélkül') +
        (t.oldest_overdue_days != null ? ` · legrégebbi: ${t.oldest_overdue_days} napja` : ''),
    });
  }
  if (t.needs_review_statements > 0) {
    rows.push({
      key: 'review', sev: 'warning', icon: Landmark, to: '/statements',
      title: 'Kivonat jóváhagyásra', count: t.needs_review_statements,
      sub: 'egyenleg-ellenőrzés szükséges',
    });
  }
  if (t.pending_drafts > 0) {
    rows.push({
      key: 'drafts', sev: 'primary', icon: Send, to: '/documents',
      title: 'Jóváhagyásra váró utalás (draft)', count: t.pending_drafts,
      sub: 'jóváhagyás a Revolut appban',
    });
  }
  // K-Q1 (2026-09-06): a NAV-adat és a számla PDF-je eltér — a NAV-ot nem írjuk felül,
  // a felhasználó dönt (a számlával van baj, vagy az olvasat rossz).
  if (t.nav_mismatch_count > 0) {
    rows.push({
      key: 'nav-mismatch', sev: 'warning', icon: AlertCircle, to: '/documents?nav_mismatch=1',
      title: 'NAV és PDF eltérés', count: t.nav_mismatch_count,
      sub: 'a NAV-adat marad; ellenőrizd a számlát, majd nyugtázd',
    });
  }
  // D4 (2026-09-06): NAV-megfelelőség — tényközlés, nem tanácsadás. A napi kör tölti.
  if (t.nav_compliance && t.nav_compliance.alerts > 0) {
    const n = t.nav_compliance;
    const parts = [];
    if (n.koma_listed === false) parts.push('nincs a KOMA-adatbázisban');
    if (n.missing_returns_open > 0) parts.push(`${n.missing_returns_open} hiányzó bevallás`);
    if (n.arrears_huf > 0) parts.push(`${Math.round(n.arrears_huf).toLocaleString('hu-HU')} Ft hátralék`);
    rows.push({
      key: 'nav-compliance', sev: 'warning', icon: AlertCircle, to: '/info/nav-adofolyoszamla',
      title: 'NAV-megfelelőség', count: n.alerts,
      sub: parts.join(' · ') + ' — a NAV lekérdezése szerint; egyeztesd a könyvelővel',
    });
  }
  if (t.closing) {
    const c = t.closing;
    rows.push({
      key: 'closing', sev: c.ready ? 'ok' : 'warning', icon: Clock, to: '/accountant',
      title: `Könyvelő-zárás · ${c.period}`,
      sub: c.ready ? 'minden kész ✓'
        : `hiányzik: ${c.missing_pdfs} PDF, ${c.missing_statements} kivonat`,
      subColor: c.ready ? 'var(--success)' : undefined,
    });
  }
  return (
    <Card title={<CardLabel>Teendők</CardLabel>}
      style={{ flex: '1 1 300px', marginBottom: 0 }}>
      {rows.length === 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10,
          padding: '0.55rem 0.65rem', borderLeft: '3px solid var(--success)',
          borderRadius: 'var(--radius)' }}>
          <span style={{ width: 30, height: 30, borderRadius: 8, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            background: 'var(--success-subtle)' }}>
            <CheckCircle2 size={15} style={{ color: 'var(--success)' }} />
          </span>
          <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 600,
            color: 'var(--success)' }}>
            Minden rendben ✓ — nincs teendő.
          </span>
        </div>
      ) : rows.map(({ key, ...r }) => <TodoRow key={key} {...r} />)}
    </Card>
  );
}

// ── Fizetendő számlák ──────────────────────────────────────────────────────
function PayablesCard({ data }) {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const payables = data.payables || [];
  const due = data.due30 || { total_huf: 0, count: 0 };
  const today = new Date().toISOString().slice(0, 10);
  const th = { textAlign: 'left', fontSize: 'var(--fs-xs)', fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)',
    padding: '0.4rem 0.6rem' };
  const td = { padding: '0.5rem 0.6rem', borderTop: '1px solid var(--border-dim)',
    fontSize: 'var(--fs-sm)', verticalAlign: 'middle' };
  return (
    <Card title={<CardLabel>Fizetendő számlák</CardLabel>}
      actions={<HeaderLink to="/documents">Bizonylatok →</HeaderLink>} noPad>
      {payables.length === 0 ? (
        <p style={{ padding: '1rem 1.1rem', fontSize: 'var(--fs-sm)',
          color: 'var(--success)' }}>Nincs fizetendő számla. ✓</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Partner</th>
                <th style={th}>Határidő</th>
                <th style={th}>Kategória</th>
                <th style={{ ...th, textAlign: 'right' }}>Összeg</th>
                <th style={th} />
              </tr>
            </thead>
            <tbody>
              {payables.map((p) => {
                const late = p.due_date && p.due_date.slice(0, 10) < today;
                return (
                  <tr key={p.id}>
                    <td style={{ ...td, fontWeight: 500 }}>{p.partner || 'Ismeretlen'}</td>
                    <td style={{ ...td, fontFamily: 'var(--mono)',
                      fontVariantNumeric: 'tabular-nums',
                      color: late ? 'var(--danger)' : 'var(--text)',
                      fontWeight: late ? 700 : 400 }}>
                      {fmtDate(p.due_date)}
                    </td>
                    <td style={td}>{p.category ? <Badge>{p.category}</Badge> : '–'}</td>
                    <td style={{ ...td, textAlign: 'right', fontFamily: 'var(--mono)',
                      fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                      {fmtMoney(p.payable_amount ?? p.amount, p.currency)}
                    </td>
                    <td style={{ ...td, textAlign: 'right', width: 1 }}>
                      <Btn variant="ghost" size="sm"
                        onClick={() => navigate(`/documents/${p.id}`)}>Megnyit</Btn>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p style={{ padding: '0.6rem 1.1rem', borderTop: '1px solid var(--border)',
        fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
        30 napon belül összesen · {due.count} számla —{' '}
        <span style={{ fontFamily: 'var(--mono)', color: 'var(--text)',
          fontVariantNumeric: 'tabular-nums' }}>{huFmt(due.total_huf)} Ft</span>
        {!isAdmin && <><br/>A bér-utalások ebben a listában nem jelennek meg.</>}
      </p>
    </Card>
  );
}

// ── Oldal ──────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { isAdmin } = useAuth();
  const [detail, setDetail] = useState(null);          // 'balances' | 'nav' | null
  const [interval_, setInterval_] = useState('week');

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: dashboardApi.summary,
    staleTime: 60000,
  });

  // ⚠ KÜLÖN query (2026-08-04, F-3). Az egyenlegek élő banki hívásra várnak —
  // mérve 13 mp csak a Raiffeisen timeoutja —, miközben a summary DB-oldala
  // 0,6 mp. Egy kérésben a teljes nyitóképernyő a leglassabb bankra várt.
  // A `retry: false` szándékos: a backend banki hibánál is 200-at ad
  // `balances_error`-ral, tehát egy 5xx itt valódi hiba, nem átmeneti.
  const { data: balancesData, isLoading: balancesLoading,
          isError: balancesError, refetch: refetchBalances } = useQuery({
    queryKey: ['dashboard-balances'],
    queryFn: dashboardApi.balances,
    staleTime: 60000,
    retry: false,
  });

  // Az előrejelzés SAJÁT query-je: tisztán DB-alapú és gyors, tehát nem kell a
  // lassú `/balances`-hoz kötni. Az egyenleg-vonal utólag „bejön" a chartba.
  const { data: forecast, isLoading: fcLoading, isError: fcError,
          refetch: refetchForecast } = useQuery({
    queryKey: ['dashboard-forecast', interval_],
    queryFn: () => dashboardApi.cashflowForecast({ days: 30, interval: interval_ }),
    staleTime: 60000,
  });

  const { data: navState } = useQuery({
    queryKey: ['nav-upo-state'],
    queryFn: navUpoApi.state,
    staleTime: 60000,
    retry: false,          // nincs beállítva → ne próbálkozzon újra
  });

  const balancesTotal = balancesData?.balances_total_huf ?? null;
  const balances = balancesData?.balances || [];
  const sandboxBanks = balancesData?.sandbox_banks || [];

  // Projekció a 30 nap végére — ez a valós, ELŐRE mutató szám a bankegyenleg
  // mellé (visszamenőleges delta nincs: egyenleg-historikum nem tárolódik).
  const projected = (balancesTotal != null && forecast?.totals)
    ? balancesTotal + forecast.totals.in_huf - forecast.totals.out_huf : null;

  // NAV-csempe csak akkor, ha VOLT már lekérdezés — egy üres „0 Ft tartozás”
  // azt sugallná, hogy tudjuk, mi van a NAV-nál.
  const navSnap = navState?.configured ? navState.snapshot : null;
  const navMain = navSnap && (
    (navSnap.tartozas || 0) > 0
      ? { label: 'Tartozás', value: navSnap.tartozas, color: 'var(--danger)' }
      : (navSnap.tulfizetes || 0) > 0
        ? { label: 'Túlfizetés', value: navSnap.tulfizetes, color: 'var(--success)' }
        : { label: 'Egyenleg', value: 0, color: 'var(--text)' });

  const todo = data?.todo || {};

  return (
    <div>
      <div style={{ marginBottom: '1.1rem' }}>
        <h1 style={{ fontSize: '1.2rem', fontWeight: 700 }}>Dashboard</h1>
        <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>
          {new Date().toLocaleDateString('hu-HU',
            { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}
        </p>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center',
          minHeight: 240, color: 'var(--text-muted)' }}>
          <Spinner size={26} />
        </div>
      ) : isError ? (
        <Alert type="danger">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12,
            justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <span>Nem sikerült betölteni a dashboard adatait.</span>
            <Btn variant="secondary" size="sm" onClick={() => refetch()}>Újra</Btn>
          </div>
        </Alert>
      ) : data && (
        <>
          {/* ── ZÓNA 1 — KPI-csík ─────────────────────────────────────── */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 14 }}>
            <KpiTile index={0} label="Rendelkezésre álló pénz"
              value={balancesTotal}
              state={balancesError ? 'error' : balancesLoading ? 'loading' : undefined}
              onRetry={() => refetchBalances()}
              accent={sandboxBanks.length > 0 ? 'var(--text-muted)' : undefined}
              chip={<SandboxChip banks={sandboxBanks} />}
              sub={projected != null
                ? `30 nap múlva várhatóan ${huCompact(projected)} Ft`
                : `${balances.length} számla · devizák MNB-középárfolyamon`}
              subColor={projected != null && projected < 0 ? 'var(--danger)' : undefined}
              footnote={sandboxBanks.length > 0
                ? `${sandboxBanks.map(bankLabel).join(' + ')} teszt-környezetben — nem valós pénz`
                : undefined}
              expand={balances.length ? {
                open: detail === 'balances',
                label: `${balances.length} számla`,
                onToggle: () => setDetail(d => d === 'balances' ? null : 'balances'),
              } : undefined} />

            <RunwayTile index={1} monthly={data.monthly} balanceTotal={balancesTotal}
              balancesReady={!balancesLoading} />

            <KpiTile index={2} label="Lejárt tartozás"
              value={todo.overdue_total_huf || 0}
              accent={todo.overdue_count > 0 ? 'var(--danger)' : 'var(--success)'}
              sub={todo.overdue_count > 0
                ? `${todo.overdue_count} számla${todo.oldest_overdue_days != null
                    ? ` · legrégebbi ${todo.oldest_overdue_days} napja` : ''}${isAdmin ? '' : ' · bér nélkül'}`
                : 'nincs lejárt tartozás ✓'}
              subColor={todo.overdue_count > 0 ? 'var(--danger)' : 'var(--success)'} />

            {navMain && (
              <KpiTile index={3} label="NAV adófolyószámla"
                value={navMain.value} accent={navMain.color}
                sub={`${navMain.label}${navSnap.pay_hidden ? ' · bér nélkül' : ''} · ${fmtDate(navSnap.queried_at)}`}
                footnote={navState.eltunt_tetelek > 0
                  ? `${navState.eltunt_tetelek} tétel kikerült a NAV válaszából`
                  : undefined}
                expand={(navState.adonemek || []).some(a => a.egyenleg) ? {
                  open: detail === 'nav',
                  label: 'Adónemek',
                  onToggle: () => setDetail(d => d === 'nav' ? null : 'nav'),
                } : undefined} />
            )}
          </div>

          <DetailPanel which={detail} balances={balances} navState={navState} />

          {/* ── ZÓNA 2 — Likviditás-előrejelzés + Teendők ─────────────── */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 14 }}>
            <ForecastCard forecast={forecast} isLoading={fcLoading} isError={fcError}
              onRetry={() => refetchForecast()} startBalance={balancesTotal}
              balancePending={balancesLoading}
              interval={interval_} setInterval_={setInterval_} />
            <TodoCard todo={data.todo} />
          </div>

          {/* ── ZÓNA 3 — Eredmény ─────────────────────────────────────────
              ⚠ EZ NEM CASH-FLOW, és korábban annak volt címkézve. A
              `/summary` `monthly` tömbje a kiállított/beérkezett SZÁMLÁKAT
              összegzi könyvelési időszak szerint, fizetés-feltétel NÉLKÜL
              (dashboard.py, GROUP BY accounting_period) — ez eredményszemléletű
              P&L. A valódi pénzforgalom a BankTransaction-ből jönne, de azon
              nincs `amount_huf` mező (devizás tételhez `tx_date`-re illesztett
              MNB-árfolyam kellene). Amíg az nincs meg, a CÍMKE a helyes válasz:
              ne nevezzük cash-flow-nak azt, ami nem az. */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 14 }}>
            <Card title={<CardLabel>Eredmény — 12 hónap</CardLabel>}
              subtitle="Kiállított és beérkezett számlák alapján — nem pénzmozgás"
              actions={<HeaderLink to="/stats">Statisztikák →</HeaderLink>}
              style={{ flex: '1 1 100%', marginBottom: 0 }}>
              <CashflowChart data={data.monthly || []} />
            </Card>
          </div>

          {/* ── ZÓNA 4 — Fizetendő számlák ────────────────────────────── */}
          <PayablesCard data={data} />
        </>
      )}
    </div>
  );
}
